import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Products } from "@/components/Products";
import { Configurator } from "@/components/Configurator";
import { HowItWorks, Faq, Contacts, Footer } from "@/components/InfoSections";
import { api, cartTotals, loadCart, saveCart } from "@/lib/api";
import { useI18n } from "@/i18n";

export default function Landing() {
  const { t, money } = useI18n();
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(false);
  const [productId, setProductId] = useState("individual");
  const [cart, setCart] = useState(loadCart);

  useEffect(() => {
    api
      .get("/catalog")
      .then((r) => {
        setCatalog(r.data);
        // refresh carts saved before price/text changes: current prices, text only at the bottom
        setCart((c) =>
          c
            .map((it) => {
              const p = r.data.products.find((x) => x.id === it.product_id);
              if (!p) return null;
              const { top_text, ...rest } = it;
              const next = { ...rest, name: p.name, unit_price: p.price };
              if (p.customizable && !(next.bottom_text || "").trim()) {
                if (!(top_text || "").trim()) return null;
                next.bottom_text = top_text.trim();
              }
              return next;
            })
            .filter(Boolean)
        );
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => saveCart(cart), [cart]);

  useEffect(() => {
    if (catalog && window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [catalog]);

  const selectProduct = (id) => {
    setProductId(id);
    document.getElementById("order")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const total = cartTotals(cart, catalog?.products || []).subtotal;

  return (
    <div className="min-h-screen bg-white text-black">
      <Header cartCount={cartCount} />
      <main>
        <Hero />
        {!catalog && !error && (
          <div className="flex items-center justify-center gap-2 py-20 text-zinc-600" data-testid="catalog-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> {t("catalog.loading")}
          </div>
        )}
        {error && (
          <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="catalog-error">
            <p className="font-display text-lg font-semibold">{t("catalog.error")}</p>
            <button onClick={() => window.location.reload()} className="rpm-btn rpm-btn-black mt-4" data-testid="catalog-retry-button">
              {t("catalog.retry")}
            </button>
          </div>
        )}
        {catalog && (
          <>
            <Products products={catalog.products} onSelect={selectProduct} />
            <Configurator catalog={catalog} productId={productId} setProductId={setProductId} cart={cart} setCart={setCart} />
          </>
        )}
        <HowItWorks />
        <Faq />
        <Contacts telegramUrl={catalog?.contact_telegram_url} phone={catalog?.contact_phone} />
      </main>
      <Footer />

      {cartCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-200 bg-white p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.06)] lg:hidden" data-testid="mobile-cart-bar">
          <a href="#checkout" className="rpm-btn rpm-btn-orange w-full" data-testid="mobile-cart-checkout-button">
            {t("checkout.mobileBar", { n: cartCount, total: money(total) })}
          </a>
        </div>
      )}
    </div>
  );
}
